(() => {
  const SELECTOR = 'restaurant-header';

  class RestaurantHeader extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.toggle = this.querySelector('.restaurant-header__menu-toggle');
      this.panel = this.querySelector('.restaurant-header__mobile-panel');
      this.overlay = this.querySelector('.restaurant-header__overlay');
      this.submenuToggles = [...this.querySelectorAll('.restaurant-header__submenu-toggle')];
      this.mobileLinks = [...this.querySelectorAll('.restaurant-header__menu--mobile > li')];
      this.lastScroll = window.scrollY;
      this.scrollTicking = false;
      this.previouslyFocused = null;

      this.mobileLinks.forEach((item, index) => item.style.setProperty('--rh-index', index));
      this.onToggle = () => this.isOpen ? this.close() : this.open();
      this.onOverlay = () => this.close();
      this.onKeydown = (event) => this.handleKeydown(event);
      this.onResize = () => { if (window.innerWidth >= 990) this.close(false); };
      this.onScroll = () => this.requestScrollUpdate();
      this.onSubmenu = (event) => this.toggleSubmenu(event.currentTarget);

      this.toggle?.addEventListener('click', this.onToggle);
      this.overlay?.addEventListener('click', this.onOverlay);
      this.submenuToggles.forEach(button => button.addEventListener('click', this.onSubmenu));
      this.mobileNavLinks = [...this.querySelectorAll('.restaurant-header__menu--mobile a')];
      this.onMobileLink = (event) => {
        const item = event.currentTarget.closest('li');
        const ownsSubmenu = item?.classList.contains('has-children') && event.currentTarget.parentElement === item.firstElementChild;
        if (!ownsSubmenu) this.close(false);
      };
      this.mobileNavLinks.forEach(link => {
        link.addEventListener('click', this.onMobileLink);
      });
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize, { passive: true });
      if (this.dataset.sticky === 'true') window.addEventListener('scroll', this.onScroll, { passive: true });
      this.updateSticky();
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.close(false);
      this.toggle?.removeEventListener('click', this.onToggle);
      this.overlay?.removeEventListener('click', this.onOverlay);
      this.submenuToggles.forEach(button => button.removeEventListener('click', this.onSubmenu));
      this.mobileNavLinks?.forEach(link => link.removeEventListener('click', this.onMobileLink));
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.initialized = false;
    }

    get isOpen() { return this.classList.contains('is-open'); }

    open() {
      if (!this.toggle || !this.panel) return;
      this.previouslyFocused = document.activeElement;
      this.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.toggle.setAttribute('aria-label', 'Fermer le menu');
      this.panel.setAttribute('aria-hidden', 'false');
      this.overlay?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('restaurant-header-lock');
      requestAnimationFrame(() => this.getFocusable()[0]?.focus());
    }

    close(restoreFocus = true) {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.toggle?.setAttribute('aria-expanded', 'false');
      this.toggle?.setAttribute('aria-label', 'Ouvrir le menu');
      this.panel?.setAttribute('aria-hidden', 'true');
      this.overlay?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('restaurant-header-lock');
      if (restoreFocus && this.previouslyFocused instanceof HTMLElement) this.previouslyFocused.focus();
    }

    toggleSubmenu(button) {
      const submenu = document.getElementById(button.getAttribute('aria-controls'));
      if (!submenu || !this.contains(submenu)) return;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!expanded));
      submenu.classList.toggle('is-open', !expanded);
    }

    getFocusable() {
      if (!this.panel) return [];
      return [...this.panel.querySelectorAll('a[href], button:not([disabled])')].filter(el => el.offsetParent !== null);
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.isOpen) { event.preventDefault(); this.close(); return; }
      if (event.key !== 'Tab' || !this.isOpen) return;
      const focusable = this.getFocusable();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    requestScrollUpdate() {
      if (this.scrollTicking) return;
      this.scrollTicking = true;
      requestAnimationFrame(() => { this.updateSticky(); this.scrollTicking = false; });
    }

    updateSticky() {
      if (this.dataset.sticky !== 'true') return;
      const current = Math.max(window.scrollY, 0);
      const delta = current - this.lastScroll;
      this.classList.toggle('is-scrolled', current > 20);
      if (!this.isOpen && current > 180 && delta > 8) this.classList.add('is-hidden');
      else if (delta < -5 || current < 80) this.classList.remove('is-hidden');
      this.lastScroll = current;
    }
  }

  if (!customElements.get(SELECTOR)) customElements.define(SELECTOR, RestaurantHeader);

  document.addEventListener('shopify:section:load', event => {
    const header = event.target.querySelector?.(SELECTOR);
    if (header && !header.isConnected) customElements.upgrade(header);
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelector?.(SELECTOR)?.close(false);
  });
})();
